import { FormattedText } from "@tm/static";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useCancelPolicyStep } from "../CancelPolicyContext";
import type {
  CancelPolicyReasonItem,
  CancelPolicyReasonSelectorData,
} from "../types";
import {
  cardSectionTitleStyle,
  ErrorText,
  OptionButton,
  OptionRow,
  optionTitleStyleDefault,
  optionTitleStyleSelected,
  RadioInner,
  RadioOuter,
  ReasonHeader,
  ReasonOptionCard,
  ReasonOptions,
  reasonSubTitleStyle,
  StepCard,
  TextArea,
  TextAreaWrap,
} from "./CancelPolicyStepCards.style";

const ReasonOption = memo(
  ({
    reason,
    isSelected,
    otherReasonText,
    otherReasonError,
    onReasonSelect,
    onOtherReasonChange,
    onOtherReasonBlur,
  }: {
    reason: CancelPolicyReasonItem;
    isSelected: boolean;
    otherReasonText: string;
    otherReasonError: string;
    onReasonSelect: (reasonId: string) => void;
    onOtherReasonChange: (value: string) => void;
    onOtherReasonBlur: () => void;
  }) => {
    const reasonId = reason.id || "";
    const shouldShowInput = Boolean(isSelected && reason.requiresInput);

    return (
      <ReasonOptionCard $selected={isSelected} $disabled={reason.disabled}>
        <OptionButton
          type="button"
          disabled={reason.disabled || !reasonId}
          onClick={() => onReasonSelect(reasonId)}
        >
          <OptionRow>
            <FormattedText
              value={reason.title}
              baseStyle={
                isSelected ? optionTitleStyleSelected : optionTitleStyleDefault
              }
            />
            <RadioOuter $selected={isSelected}>
              {isSelected ? <RadioInner /> : null}
            </RadioOuter>
          </OptionRow>
        </OptionButton>

        {shouldShowInput ? (
          <>
            <TextAreaWrap $hasError={Boolean(otherReasonError)}>
              <TextArea
                value={otherReasonText}
                onChange={(event) => onOtherReasonChange(event.target.value)}
                onBlur={onOtherReasonBlur}
                placeholder={reason.inputConfig?.placeholder}
                maxLength={reason.inputConfig?.maxLength}
                aria-invalid={Boolean(otherReasonError)}
              />
            </TextAreaWrap>
            {otherReasonError ? (
              <ErrorText>{otherReasonError}</ErrorText>
            ) : null}
          </>
        ) : null}
      </ReasonOptionCard>
    );
  }
);

export const CancelPolicyReasonSelector = ({
  header,
  reasons,
  validation,
}: CancelPolicyReasonSelectorData) => {
  const { activeStepId, onStepStateChange, stepStateById } =
    useCancelPolicyStep();
  const savedState = activeStepId
    ? stepStateById?.[activeStepId]?.value
    : undefined;
  const savedReasonId =
    typeof savedState?.["reasonId"] === "string" ? savedState["reasonId"] : "";
  const savedReasonText =
    typeof savedState?.["reasonText"] === "string"
      ? savedState["reasonText"]
      : "";
  const [selectedReasonId, setSelectedReasonId] = useState(
    savedReasonId || reasons?.find((r) => r.selected && r.id)?.id || ""
  );
  const [otherReasonText, setOtherReasonText] = useState(savedReasonText);
  const [isOtherReasonTouched, setIsOtherReasonTouched] = useState(false);

  useEffect(() => {
    const next =
      savedReasonId || reasons?.find((r) => r.selected && r.id)?.id || "";
    setSelectedReasonId((prev) => (prev !== next ? next : prev));
  }, [reasons, savedReasonId]);

  useEffect(() => {
    setOtherReasonText((prev) =>
      prev !== savedReasonText ? savedReasonText : prev
    );
  }, [savedReasonText]);

  const selectedReason = reasons?.find(
    (reason) => reason.id === selectedReasonId
  );
  const isOtherReasonSelected = Boolean(selectedReason?.requiresInput);
  const normalizedOtherReasonText = otherReasonText.trim();
  const otherReasonPattern = selectedReason?.inputConfig?.validation?.pattern;

  // Compile the API-provided regex once — only recompile when the pattern string changes.
  const compiledPattern = useMemo(() => {
    if (!otherReasonPattern) return null;
    try {
      return new RegExp(otherReasonPattern);
    } catch {
      // Invalid pattern from API: skip validation.
      return null;
    }
  }, [otherReasonPattern]);

  let isOtherReasonValid = true;

  if (isOtherReasonSelected) {
    const isRequired = selectedReason?.inputConfig?.isRequired !== false;

    if (isRequired && !normalizedOtherReasonText) {
      isOtherReasonValid = false;
    } else if (compiledPattern) {
      isOtherReasonValid = compiledPattern.test(normalizedOtherReasonText);
    }
  }

  const otherReasonError =
    isOtherReasonSelected && isOtherReasonTouched && !isOtherReasonValid
      ? selectedReason?.inputConfig?.validation?.errorMessage || ""
      : "";
  const requiresSelection = validation?.isRequired !== false;
  const isValid =
    (requiresSelection ? Boolean(selectedReasonId) : true) &&
    isOtherReasonValid;

  const handleReasonSelect = useCallback((reasonId: string) => {
    setSelectedReasonId(reasonId);
    setIsOtherReasonTouched(false);
  }, []);

  const handleOtherReasonBlur = useCallback(() => {
    setIsOtherReasonTouched(true);
  }, []);

  useEffect(() => {
    onStepStateChange?.({
      isValid,
      value: {
        reasonId: selectedReasonId,
        reasonText: isOtherReasonSelected ? normalizedOtherReasonText : "",
      },
    });
  }, [
    isOtherReasonSelected,
    isValid,
    normalizedOtherReasonText,
    onStepStateChange,
    selectedReasonId,
  ]);

  return (
    <StepCard>
      <ReasonHeader>
        <FormattedText
          value={header?.title}
          baseStyle={cardSectionTitleStyle}
        />
        {header?.subTitle ? (
          <FormattedText
            value={header.subTitle}
            baseStyle={reasonSubTitleStyle}
          />
        ) : null}
      </ReasonHeader>

      <ReasonOptions>
        {reasons?.map((reason, index) => {
          const isSelected = selectedReasonId === reason.id;
          return (
            <ReasonOption
              key={reason.id || `${index}`}
              reason={reason}
              isSelected={isSelected}
              otherReasonText={isSelected ? otherReasonText : ""}
              otherReasonError={isSelected ? otherReasonError : ""}
              onReasonSelect={handleReasonSelect}
              onOtherReasonChange={setOtherReasonText}
              onOtherReasonBlur={handleOtherReasonBlur}
            />
          );
        })}
      </ReasonOptions>
    </StepCard>
  );
};

export default CancelPolicyReasonSelector;
